import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReCAPTCHA from 'react-google-recaptcha';
import { validateEmail } from '../../utils/validation';
import { API_ENDPOINTS } from '../../config/api';
import { authUtils } from '../../utils/auth';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    const recaptchaToken = await recaptchaRef.current?.executeAsync();
    if (!recaptchaToken) {
      setError('Please complete the CAPTCHA verification');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, {
        email,
        recaptchaToken,
      });

      if (response.status === 200) {
        authUtils.setEmail(email);
        navigate('/reset-password-code');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send reset code. Please try again.');
      recaptchaRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-jobesta-gray-20 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-jobesta shadow-jobesta-card">
        <div>
          <h1 className="text-center text-3xl mb-2 tracking-tight">
            <span className="font-black text-jobesta-blue">Job</span>
            <span className="font-extralight text-jobesta-blue">esta</span>
          </h1>
          <h2 className="text-center text-xl font-semibold text-jobesta-gray-90">
            Reset your password
          </h2>
          <p className="mt-2 text-center text-sm text-jobesta-gray-60">
            Enter your email address and we'll send you a verification code
          </p>
        </div>
        <form className="mt-8 space-y-6 ph-no-capture" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="email" className="sr-only">
              Email Address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              className="appearance-none relative block w-full px-3 py-2 border border-jobesta-gray-40 placeholder-jobesta-gray-60 text-jobesta-gray-90 rounded-jobesta focus:outline-none focus:ring-2 focus:ring-jobesta-lightblue focus:ring-opacity-30 focus:border-jobesta-blue focus:z-10 sm:text-sm"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          {error && (
            <div className="rounded-jobesta bg-jobesta-red bg-opacity-10 p-4 border border-jobesta-red">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-jobesta-red">{error}</h3>
                </div>
              </div>
            </div>
          )}

          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''}
            size="invisible"
          />

          <div className="flex items-center justify-center">
            <Link
              to="/login"
              className="text-sm text-jobesta-blue hover:text-jobesta-darkblue hover:underline transition-colors"
            >
              Back to login
            </Link>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading || !validateEmail(email)}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-jobesta text-white bg-jobesta-blue hover:bg-jobesta-darkblue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-jobesta-lightblue transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Sending...' : 'Send Reset Code'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForgotPassword;